import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import { Pagination } from 'components/Pagination/Pagination'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { formatGhs } from 'pages/FuelManagement/Shared/fuelFormatters'
import { Flex, PageFooter } from 'styling/GlobalStyles'
import { OtherText } from 'styling/Typography'
import { formatDateForDisplay, handlePagination } from 'utils/helpers'
import { IGetAllInventoryAssignmentsSingleResponse } from 'types/api/inventoryItems.interface'
import { IInventoryMovementResponse } from 'types/api/inventoryControlApi.interface'
import { IInventoryIssueRow } from 'types/inventoryControl.interface'
import {
  IStoreCounterKindFilter,
  IStoreHistoryTabProps,
  IStoreLedgerRow,
} from 'types/inventoryCounter.interface'
import { InventoryMovementKind } from 'types/inventoryEnums.interface'
import { ViewInventoryIssueModal } from '../Issues/ViewInventoryIssueModal'
import { ViewInventoryAssignmentModal } from '../Assignments/ViewInventoryAssignmentModal'
import { chargeToDisplay, counterLinesSummary } from '../Shared/inventoryFormatters'
import { COUNTER_KIND_META, MOVEMENT_KIND_LABEL } from '../Shared/inventoryEnumMaps'
import { KindFilterBar, KindFilterPill, LedgerMetaText, LedgerValueText } from './StoreCounter.styled'

const KIND_FILTERS: IStoreCounterKindFilter[] = [
  { id: 'ALL', label: 'All' },
  { id: 'ISSUED', label: 'Issued' },
  { id: 'ASSIGNED', label: 'Assigned' },
  { id: 'RETURNED', label: 'Returned' },
]

const movementKindToLedger = (
  kind: InventoryMovementKind,
): IStoreLedgerRow['kind'] | null => {
  if (kind === InventoryMovementKind.Issue) return 'ISSUED'
  if (kind === InventoryMovementKind.Assign) return 'ASSIGNED'
  if (kind === InventoryMovementKind.Return) return 'RETURNED'
  return null
}

const formatMovementWorker = (
  worker: unknown,
  actor?: { firstName?: string; lastName?: string } | null,
): string | null => {
  if (typeof worker === 'string') return worker
  if (worker && typeof worker === 'object') {
    const obj = worker as { firstName?: string; lastName?: string; name?: string }
    if (obj.name) return obj.name
    const full = [obj.firstName, obj.lastName].filter(Boolean).join(' ').trim()
    if (full) return full
  }
  if (actor) {
    const full = [actor.firstName, actor.lastName].filter(Boolean).join(' ').trim()
    if (full) return full
  }
  return null
}

const mapMovement = (row: IInventoryMovementResponse): IStoreLedgerRow | null => {
  const kind = movementKindToLedger(row.kind)
  if (!kind) return null
  return {
    id: `MOV-${row.id}`,
    kind,
    dateISO: row.at,
    worker: formatMovementWorker(row.worker as unknown, row.actor),
    itemsSummary: `${row.item} ×${Math.abs(row.qty)}`,
    chargeLabel: row.chargeTo ? chargeToDisplay(row.chargeTo) : null,
    dueBackLabel: null,
    valueGhs: row.valueGhs,
  }
}

const mapIssue = (issue: IInventoryIssueRow): IStoreLedgerRow => ({
  id: `ISS-${issue.id}`,
  kind: 'ISSUED',
  dateISO: issue.date,
  worker: issue.assignedForUser
    ? `${issue.assignedForUser.firstName} ${issue.assignedForUser.lastName}`
    : null,
  itemsSummary: counterLinesSummary(
    (issue.lines || []).map((line) => ({
      quantity: line.quantity,
      name: line.inventoryItem?.name ?? 'Item',
    })),
  ),
  chargeLabel: issue.chargeTo
    ? chargeToDisplay(issue.chargeTo)
    : issue.control
      ? chargeToDisplay(issue.control.chargeTo)
      : null,
  dueBackLabel: null,
  valueGhs: issue.valueGhs ?? issue.control?.valueGhs ?? null,
  issueRef: issue,
})

const mapAssignment = (assignment: IGetAllInventoryAssignmentsSingleResponse): IStoreLedgerRow => ({
  id: `ASG-${assignment.id}`,
  kind: assignment.type == 2 ? 'RETURNED' : 'ASSIGNED',
  dateISO: assignment.createdAt,
  worker: assignment.assignee
    ? `${assignment.assignee.firstName} ${assignment.assignee.lastName}`
    : null,
  itemsSummary: counterLinesSummary(
    (assignment.lines || []).map((line) => ({
      quantity: line.quantity,
      name: line.inventoryItem?.name ?? 'Item',
    })),
  ),
  chargeLabel: null,
  dueBackLabel: assignment.dueBackAt
    ? `Due back ${formatDateForDisplay(assignment.dueBackAt)}`
    : null,
  valueGhs: null,
  assignmentRef: assignment,
})

export const HistoryTab = ({ localLedger, params, setParams }: IStoreHistoryTabProps) => {
  const { issues, totalPages } = useSelector((store: RootState) => store.inventoryIssues)
  const { assignments } = useSelector((store: RootState) => store.inventoryItems)
  const { movements } = useSelector((store: RootState) => store.inventoryControl)
  const [kindFilter, setKindFilter] = useState<IStoreCounterKindFilter['id']>('ALL')
  const [issueModal, setIssueModal] = useState<{ open: boolean; value: IInventoryIssueRow }>({
    open: false,
    value: null,
  })
  const [assignmentModal, setAssignmentModal] = useState<{
    open: boolean
    value: IGetAllInventoryAssignmentsSingleResponse
  }>({ open: false, value: null })

  const rows = useMemo(() => {
    const movementRows = (movements.data ?? [])
      .map(mapMovement)
      .filter(Boolean) as IStoreLedgerRow[]
    const fallbackRows =
      movementRows.length > 0
        ? []
        : [...issues.map(mapIssue), ...(assignments?.data || []).map(mapAssignment)]
    return [...localLedger, ...movementRows, ...fallbackRows]
      .filter((row) => kindFilter === 'ALL' || row.kind === kindFilter)
      .sort((a, b) => new Date(b.dateISO).getTime() - new Date(a.dateISO).getTime())
  }, [issues, assignments, localLedger, kindFilter, movements.data])

  const columns: IHonestTableColumn<IStoreLedgerRow>[] = [
    {
      id: 'kind',
      header: 'KIND',
      cell: (row) => (
        <StatusPill status={COUNTER_KIND_META[row.kind]?.tone ?? 'neutral'}>
          {COUNTER_KIND_META[row.kind]?.label ?? MOVEMENT_KIND_LABEL[row.kind] ?? row.kind}
        </StatusPill>
      ),
    },
    { id: 'date', header: 'DATE', cell: (row) => formatDateForDisplay(row.dateISO, true) },
    {
      id: 'worker',
      header: 'WORKER',
      cell: (row) => (row.worker ? row.worker : <OtherText grey>—</OtherText>),
    },
    { id: 'items', header: 'ITEMS', minWidth: '14rem', cell: (row) => row.itemsSummary },
    {
      id: 'meta',
      header: 'CHARGED TO / DUE BACK',
      minWidth: '12rem',
      cell: (row) =>
        row.chargeLabel || row.dueBackLabel ? (
          <LedgerMetaText>{row.chargeLabel || row.dueBackLabel}</LedgerMetaText>
        ) : (
          <OtherText grey>—</OtherText>
        ),
    },
    {
      id: 'value',
      header: 'VALUE',
      align: 'right',
      cell: (row) =>
        row.valueGhs != null ? (
          <LedgerValueText>{formatGhs(row.valueGhs)}</LedgerValueText>
        ) : (
          <OtherText grey>—</OtherText>
        ),
    },
  ]

  const handleRowClick = (row: IStoreLedgerRow) => {
    if (row.issueRef) setIssueModal({ open: true, value: row.issueRef })
    else if (row.assignmentRef) setAssignmentModal({ open: true, value: row.assignmentRef })
  }

  const pageCount = movements.totalPages || totalPages || 1

  return (
    <>
      <Flex mb='16'>
        <KindFilterBar>
          {KIND_FILTERS.map((filter) => (
            <KindFilterPill
              key={filter.id}
              type='button'
              $active={kindFilter === filter.id}
              onClick={() => setKindFilter(filter.id)}
            >
              {filter.label}
            </KindFilterPill>
          ))}
        </KindFilterBar>
      </Flex>
      {!rows.length ? (
        <NoItems title='Movements' />
      ) : (
        <HonestTable columns={columns} data={rows} keyField='id' onClick={handleRowClick} />
      )}
      <PageFooter>
        <Pagination
          setPage={(page) => handlePagination(page, setParams, 30)}
          pageCount={Math.ceil(pageCount)}
          forcePage={(params.page ? params.page : 1) - 1}
        />
      </PageFooter>
      {issueModal.open && (
        <ViewInventoryIssueModal
          value={issueModal.value}
          closeModal={() => setIssueModal({ value: null, open: false })}
        />
      )}
      {assignmentModal.open && (
        <ViewInventoryAssignmentModal
          value={assignmentModal.value}
          closeModal={() => setAssignmentModal({ value: null, open: false })}
        />
      )}
    </>
  )
}
